import { idbCount, idbDeleteMany, idbGet, idbGetAll, idbPut, STORES } from './database';

/**
 * Memoized output of B4's semantic compressor. Keyed by a hash of
 * (text + rate + model version) — see src/nlp/compressor.ts — so the same
 * docstring compressed at the same rate is only ever run through the model once
 * across the whole app's lifetime, and a model version bump invalidates
 * everything implicitly rather than needing a sweep.
 */
export interface CachedCompression {
  hash: string;
  /** The compressed text. */
  text: string;
  tokensBefore: number;
  tokensAfter: number;
}

/**
 * What actually lands in the store. `cachedAt` exists only to order eviction and is stamped here, not
 * by the caller, so the write contract stays the plain CachedCompression above.
 */
interface StoredCompression extends CachedCompression {
  /** Absent on entries written before the cap existed; those sort as oldest and go first. */
  cachedAt?: number;
}

/**
 * Cap on retained entries. The key includes the source text, so every edit to a docstring orphans its
 * old entry forever — growth tracks editing activity, not project size, and never levels off on its
 * own. At roughly 0.5 KB per entry this bounds the store near 1 MB.
 *
 * Eviction is always safe here in a way it never is elsewhere in src/db: this store is derived data,
 * so a wrong guess costs one recompute of a span that is already fail-open. It holds nothing that
 * could not be rebuilt from the user's files.
 */
const MAX_ENTRIES = 1500;

/** Prune down to here rather than to MAX_ENTRIES, so the scan runs once per ~300 writes, not on every write. */
const PRUNE_TARGET = 1200;

export async function getCachedCompression(hash: string): Promise<CachedCompression | undefined> {
  return idbGet<CachedCompression>(STORES.compressorCache, hash);
}

export async function saveCachedCompression(entry: CachedCompression): Promise<void> {
  const record: StoredCompression = { ...entry, cachedAt: Date.now() };
  await idbPut(STORES.compressorCache, record);
  try {
    await pruneToTarget(record.hash);
  } catch {
    // Housekeeping only. The entry above is already written, so don't report a failed save.
  }
}

/**
 * Drops the oldest entries once the store runs over MAX_ENTRIES. The full read only happens on the
 * rare write that trips the cap — the common path pays a count() and nothing else.
 *
 * `keepHash` is the entry just written. Bulk writes can share a millisecond, which leaves cachedAt
 * tied and the sort order among ties arbitrary, so exclude it explicitly rather than let a write
 * evict itself.
 */
async function pruneToTarget(keepHash: string): Promise<void> {
  if ((await idbCount(STORES.compressorCache)) <= MAX_ENTRIES) return;
  const all = await idbGetAll<StoredCompression>(STORES.compressorCache);
  const evictable = all.filter((e) => e.hash !== keepHash).sort((a, b) => (a.cachedAt ?? 0) - (b.cachedAt ?? 0));
  const evict = evictable.slice(0, Math.max(0, all.length - PRUNE_TARGET)).map((e) => e.hash);
  await idbDeleteMany(STORES.compressorCache, evict);
}
